import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import './StatsPage.css';

function StatsPage({ activeTab = 'players' }) {
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);

  // 선수 화면 상태
  const [playerTab, setPlayerTab] = useState('attendance'); // TOP 3 탭: 'attendance', 'goals'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // 'all', 'attended', 'absent'

  // 경기 화면 상태
  const [expandedMatchId, setExpandedMatchId] = useState(null);

  // 찬조 화면 상태
  const [donationSearchQuery, setDonationSearchQuery] = useState('');
  const [donationPeriod, setDonationPeriod] = useState('all'); // 'all', '1month', '3months', '6months', '1year'

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showPlayerModal) {
        setShowPlayerModal(false);
      }
    };

    if (showPlayerModal) {
      document.addEventListener('keydown', handleEscape);
      // 모달이 열릴 때 body 스크롤 방지
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showPlayerModal]);

  const fetchData = async () => {
    try {
      const playersSnapshot = await getDocs(collection(db, 'players'));
      const playersData = playersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      playersData.sort((a, b) => a.name.localeCompare(b.name, 'ko'));

      const matchesSnapshot = await getDocs(collection(db, 'matches'));
      const matchesData = matchesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      matchesData.sort((a, b) => new Date(b.date) - new Date(a.date));

      const donationsSnapshot = await getDocs(collection(db, 'donations'));
      const donationsData = donationsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      donationsData.sort((a, b) => new Date(b.date) - new Date(a.date));

      setPlayers(playersData);
      setMatches(matchesData);
      setDonations(donationsData);

      calculateStats(playersData, matchesData, donationsData);
      setLoading(false);
    } catch (error) {
      console.error('데이터 가져오기 오류:', error);
      setLoading(false);
    }
  };

  const calculateStats = (playersData, matchesData, donationsData) => {
    const filteredPlayers = playersData.filter(player =>
      player.name !== '시민리그 찬조잔여금액' && player.name !== '용병'
    );

    const statsData = filteredPlayers.map(player => {
      const totalMatches = matchesData.length;
      let attendedMatches = 0;
      let lateCount = 0;
      let noShowCount = 0;

      matchesData.forEach(match => {
        if (match.attendanceStatus) {
          const status = match.attendanceStatus[player.id];
          if (status === 'present') {
            attendedMatches++;
          } else if (status === 'late') {
            attendedMatches++;
            lateCount++;
          } else if (status === 'no-show') {
            noShowCount++;
          }
        } else if (match.attendance && match.attendance.includes(player.id)) {
          attendedMatches++;
        }
      });

      const totalGoals = matchesData.reduce((total, match) => {
        if (match.quarters) {
          return total + match.quarters.reduce((quarterTotal, quarter) => {
            return quarterTotal + quarter.ourGoals.reduce((goalTotal, goal) => {
              return goal.playerId === player.id ? goalTotal + goal.count : goalTotal;
            }, 0);
          }, 0);
        }
        if (match.goals) {
          const playerGoal = match.goals.find(g => g.playerId === player.id);
          return total + (playerGoal ? playerGoal.count : 0);
        }
        return total;
      }, 0);

      const totalDonations = donationsData.reduce((total, donation) => {
        if (donation.contributors) {
          const contribution = donation.contributors.find(c => c.playerId === player.id);
          return total + (contribution ? contribution.amount : 0);
        }
        return total;
      }, 0);

      const attendanceRate = totalMatches > 0 ? Math.round(attendedMatches / totalMatches * 100) : 0;

      return {
        id: player.id,
        name: player.name,
        attendedMatches,
        totalMatches,
        attendanceRate,
        lateCount,
        noShowCount,
        totalGoals,
        totalDonations
      };
    });

    setStats(statsData);
  };

  // 정렬 핸들러
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortedStats = () => {
    let filtered = stats;

    // 검색 필터
    if (searchQuery.trim()) {
      filtered = filtered.filter(stat =>
        stat.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // 정렬
    const sorted = [...filtered].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      if (sortBy === 'name') {
        return sortOrder === 'desc'
          ? bValue.localeCompare(aValue, 'ko')
          : aValue.localeCompare(bValue, 'ko');
      }

      return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    });

    return sorted;
  };

  const getMatchResult = (match) => {
    const ourGoals = getOurGoals(match);
    const theirGoals = getOpponentGoals(match);

    if (ourGoals > theirGoals) return '승';
    if (ourGoals < theirGoals) return '패';
    if (ourGoals === theirGoals && (ourGoals > 0 || theirGoals > 0)) return '무';
    return '-';
  };

  const getOurGoals = (match) => {
    if (match.quarters) {
      return match.quarters.reduce((total, quarter) => {
        const playerGoals = quarter.ourGoals.reduce((sum, g) => sum + g.count, 0);
        const ownGoals = quarter.opponentOwnGoals || 0;
        return total + playerGoals + ownGoals;
      }, 0);
    }
    if (!match.goals) return 0;
    return match.goals.reduce((total, g) => total + g.count, 0);
  };

  const getOpponentGoals = (match) => {
    if (match.quarters) {
      return match.quarters.reduce((total, quarter) => total + quarter.opponentGoals, 0);
    }
    return match.opponentGoals !== undefined ? match.opponentGoals : 0;
  };

  const getPlayerName = (playerId) => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : '알 수 없음';
  };

  const toggleMatchExpand = (matchId) => {
    setExpandedMatchId(expandedMatchId === matchId ? null : matchId);
  };

  // 선수 클릭 핸들러
  const handlePlayerClick = (playerId) => {
    const player = players.find(p => p.id === playerId);
    if (!player) return;

    // 해당 선수의 경기별 히스토리 생성
    const playerHistory = matches.map(match => {
      // 출석 상태
      let attendanceStatus = 'absent';
      if (match.attendanceStatus) {
        attendanceStatus = match.attendanceStatus[playerId] || 'absent';
      } else if (match.attendance && match.attendance.includes(playerId)) {
        attendanceStatus = 'present';
      }

      // 득점 기록
      let goals = 0;
      if (match.quarters) {
        goals = match.quarters.reduce((total, quarter) => {
          return total + quarter.ourGoals.reduce((sum, goal) => {
            return goal.playerId === playerId ? sum + goal.count : sum;
          }, 0);
        }, 0);
      } else if (match.goals) {
        const playerGoal = match.goals.find(g => g.playerId === playerId);
        goals = playerGoal ? playerGoal.count : 0;
      }

      return {
        matchId: match.id,
        date: match.date,
        opponent: match.opponent || '상대팀',
        attendanceStatus,
        goals
      };
    });

    setSelectedPlayer({
      ...player,
      history: playerHistory
    });
    setHistoryFilter('all'); // 필터 초기화
    setShowPlayerModal(true);
  };

  // 히스토리 필터링
  const getFilteredHistory = (history) => {
    if (!history) return [];

    if (historyFilter === 'attended') {
      // 참석: present, late
      return history.filter(h => h.attendanceStatus === 'present' || h.attendanceStatus === 'late');
    } else if (historyFilter === 'absent') {
      // 불참: no-show, absent
      return history.filter(h => h.attendanceStatus === 'no-show' || h.attendanceStatus === 'absent');
    }

    // 전체
    return history;
  };

  // 출석 상태 라벨
  const getAttendanceLabel = (status) => {
    const labels = {
      'present': '출석',
      'late': '지각',
      'no-show': '무단불참',
      'absent': '결석'
    };
    return labels[status] || '알 수 없음';
  };

  // 출석 상태 클래스
  const getAttendanceClass = (status) => {
    return `attendance-badge ${status}`;
  };

  if (loading) {
    return <div className="stats-page"><div className="loading">로딩 중...</div></div>;
  }

  // 선수 통계 화면
  if (activeTab === 'players') {
    const sortedStats = getSortedStats();
    const top3Stats = playerTab === 'attendance'
      ? [...stats].sort((a, b) => b.attendanceRate - a.attendanceRate).slice(0, 3)
      : [...stats].sort((a, b) => b.totalGoals - a.totalGoals).slice(0, 3);

    return (
      <div className="stats-page">
        <div className="page-header">
          <h2>선수 통계</h2>
        </div>

        {/* TOP 3 */}
        <div className="top3-section">
          <div className="top3-tabs">
            <button
              className={playerTab === 'attendance' ? 'active' : ''}
              onClick={() => setPlayerTab('attendance')}
              aria-label="출석률 TOP 3"
            >
              출석률 TOP 3
            </button>
            <button
              className={playerTab === 'goals' ? 'active' : ''}
              onClick={() => setPlayerTab('goals')}
              aria-label="득점 TOP 3"
            >
              득점 TOP 3
            </button>
          </div>

          <div className="top3-list">
            {top3Stats.map((stat, index) => (
              <div key={stat.id} className={`top3-item rank-${index + 1}`}>
                <div className="rank-badge">{index + 1}</div>
                <div className="player-info">
                  <span className="player-name">{stat.name}</span>
                  <span className="player-detail">
                    {playerTab === 'attendance'
                      ? `${stat.attendedMatches}/${stat.totalMatches}경기`
                      : `총 ${stat.totalGoals}골`
                    }
                  </span>
                </div>
                <div className="stat-value">
                  {playerTab === 'attendance' ? `${stat.attendanceRate}%` : stat.totalGoals}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 검색 */}
        <div className="search-section">
          <input
            type="text"
            placeholder="선수 이름 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
            aria-label="선수 이름 검색"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="search-clear"
              aria-label="검색어 지우기"
            >
              ✕
            </button>
          )}
        </div>

        {/* 선수 목록 - 표 뷰 */}
        {sortedStats.length === 0 ? (
          <div className="no-data">
            {searchQuery ? '검색 결과가 없습니다.' : '등록된 선수가 없습니다.'}
          </div>
        ) : (
          <div className="stats-table-container">
            <table className="stats-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('name')}>
                    이름
                    {sortBy === 'name' && (
                      <span className="sort-icon">{sortOrder === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('attendanceRate')}>
                    출석률
                    {sortBy === 'attendanceRate' && (
                      <span className="sort-icon">{sortOrder === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('attendedMatches')}>
                    출석
                    {sortBy === 'attendedMatches' && (
                      <span className="sort-icon">{sortOrder === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('lateCount')}>
                    지각
                    {sortBy === 'lateCount' && (
                      <span className="sort-icon">{sortOrder === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('noShowCount')}>
                    무단불참
                    {sortBy === 'noShowCount' && (
                      <span className="sort-icon">{sortOrder === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('totalGoals')}>
                    득점
                    {sortBy === 'totalGoals' && (
                      <span className="sort-icon">{sortOrder === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </th>
                  <th className="sortable" onClick={() => handleSort('totalDonations')}>
                    찬조금
                    {sortBy === 'totalDonations' && (
                      <span className="sort-icon">{sortOrder === 'desc' ? ' ▼' : ' ▲'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStats.map((stat) => (
                  <tr key={stat.id}>
                    <td
                      className="player-name-cell clickable"
                      onClick={() => handlePlayerClick(stat.id)}
                      title="클릭하여 상세 히스토리 보기"
                    >
                      {stat.name}
                    </td>
                    <td className="primary-cell">{stat.attendanceRate}%</td>
                    <td>{stat.attendedMatches}/{stat.totalMatches}</td>
                    <td className={stat.lateCount > 0 ? 'warning-cell' : ''}>{stat.lateCount}</td>
                    <td className={stat.noShowCount > 0 ? 'danger-cell' : ''}>{stat.noShowCount}</td>
                    <td>{stat.totalGoals}</td>
                    <td>{stat.totalDonations.toLocaleString()}원</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* 선수 상세 모달 */}
        {showPlayerModal && selectedPlayer && (
          <div
            className="modal-overlay"
            onClick={() => setShowPlayerModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
          >
            <div className="modal-content player-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 id="modal-title">{selectedPlayer.name} 상세 기록</h3>
                <button
                  type="button"
                  className="close-modal-btn"
                  onClick={() => setShowPlayerModal(false)}
                  aria-label="모달 닫기"
                >
                  <span aria-hidden="true">✕</span>
                </button>
              </div>

              <div className="modal-body">
                {/* 전체 요약 */}
                <div className="player-summary">
                  <div className="summary-stat">
                    <span className="summary-label">총 경기</span>
                    <span className="summary-value">{selectedPlayer.history.length}경기</span>
                  </div>
                  <div className="summary-stat">
                    <span className="summary-label">출석</span>
                    <span className="summary-value">
                      {selectedPlayer.history.filter(h => h.attendanceStatus === 'present' || h.attendanceStatus === 'late').length}경기
                    </span>
                  </div>
                  <div className="summary-stat">
                    <span className="summary-label">총 득점</span>
                    <span className="summary-value">
                      {selectedPlayer.history.reduce((sum, h) => sum + h.goals, 0)}골
                    </span>
                  </div>
                </div>

                {/* 경기별 히스토리 */}
                <div className="history-section">
                  <div className="history-header">
                    <h4>경기별 기록</h4>
                    <div className="history-filter-tabs">
                      <button
                        className={historyFilter === 'all' ? 'active' : ''}
                        onClick={() => setHistoryFilter('all')}
                      >
                        전체 ({selectedPlayer.history.length})
                      </button>
                      <button
                        className={historyFilter === 'attended' ? 'active' : ''}
                        onClick={() => setHistoryFilter('attended')}
                      >
                        참석 ({selectedPlayer.history.filter(h => h.attendanceStatus === 'present' || h.attendanceStatus === 'late').length})
                      </button>
                      <button
                        className={historyFilter === 'absent' ? 'active' : ''}
                        onClick={() => setHistoryFilter('absent')}
                      >
                        불참 ({selectedPlayer.history.filter(h => h.attendanceStatus === 'no-show' || h.attendanceStatus === 'absent').length})
                      </button>
                    </div>
                  </div>
                  <div className="history-list">
                    {getFilteredHistory(selectedPlayer.history).map((record) => (
                      <div key={record.matchId} className="history-item">
                        <div className="history-date">
                          {record.date.replace(/^20/, '')}
                        </div>
                        <div className="history-details">
                          <div className="history-opponent">vs {record.opponent}</div>
                          <div className="history-stats">
                            <span className={getAttendanceClass(record.attendanceStatus)}>
                              {getAttendanceLabel(record.attendanceStatus)}
                            </span>
                            {record.goals > 0 && (
                              <span className="goals-badge">⚽ {record.goals}골</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {getFilteredHistory(selectedPlayer.history).length === 0 && (
                      <div className="no-data">
                        {historyFilter === 'all' && '경기 기록이 없습니다.'}
                        {historyFilter === 'attended' && '참석한 경기가 없습니다.'}
                        {historyFilter === 'absent' && '불참한 경기가 없습니다.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 경기 기록 화면
  if (activeTab === 'matches') {
    const totalWins = matches.filter(m => getMatchResult(m) === '승').length;
    const totalLosses = matches.filter(m => getMatchResult(m) === '패').length;
    const totalDraws = matches.filter(m => getMatchResult(m) === '무').length;
    const totalMatches = totalWins + totalLosses + totalDraws;
    const winRate = totalMatches > 0 ? ((totalWins / totalMatches) * 100).toFixed(1) : 0;
    const totalGoalsScored = matches.reduce((sum, m) => sum + getOurGoals(m), 0);
    const totalGoalsConceded = matches.reduce((sum, m) => sum + getOpponentGoals(m), 0);

    return (
      <div className="stats-page">
        <div className="page-header">
          <h2>경기 기록</h2>
        </div>

        {/* 시즌 요약 - 간결하게 */}
        <div className="season-summary">
          <div className="summary-item">
            <span className="summary-label">전적</span>
            <span className="summary-value">
              {totalWins}승 {totalDraws}무 {totalLosses}패
            </span>
            <span className="summary-subvalue">
              승률 {winRate}%
            </span>
          </div>
          <div className="summary-item">
            <span className="summary-label">득실</span>
            <span className="summary-value">
              {totalGoalsScored} : {totalGoalsConceded}
            </span>
          </div>
        </div>

        {/* 경기 목록 */}
        {matches.length === 0 ? (
          <div className="no-data">등록된 경기가 없습니다.</div>
        ) : (
          <div className="match-list">
            {matches.map((match) => {
              const ourGoals = getOurGoals(match);
              const theirGoals = getOpponentGoals(match);
              const result = getMatchResult(match);
              const isExpanded = expandedMatchId === match.id;
              const hasDetails = match.quarters && match.quarters.length > 0;

              return (
                <div key={match.id} className={`match-card result-${result}`}>
                  <div className="match-card-header">
                    <span className="match-date">{match.date.replace(/^20/, '')}</span>
                    <span className={`match-result ${result}`}>{result}</span>
                  </div>

                  <div className="match-score">
                    <span className="team-name">동국FC</span>
                    <span className="score">
                      <span className="our-score">{ourGoals}</span>
                      <span className="score-sep">:</span>
                      <span className="their-score">{theirGoals}</span>
                    </span>
                    <span className="team-name opponent">{match.opponent || '상대팀'}</span>
                  </div>

                  {hasDetails && (
                    <button
                      onClick={() => toggleMatchExpand(match.id)}
                      className="expand-button"
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? '상세 정보 접기' : '상세 정보 보기'}
                    >
                      {isExpanded ? '상세 접기 ▲' : '상세 보기 ▼'}
                    </button>
                  )}

                  {isExpanded && hasDetails && (
                    <div className="match-details">
                      {match.quarters.map((quarter, index) => {
                        const qGoals = quarter.ourGoals.reduce((sum, g) => sum + g.count, 0);
                        const qOwnGoals = quarter.opponentOwnGoals || 0;
                        const qTotal = qGoals + qOwnGoals;

                        return (
                          <div key={index} className="quarter-info">
                            <div className="quarter-header">
                              <span className="quarter-name">{quarter.quarterNumber}쿼터</span>
                              <span className="quarter-score">{qTotal} : {quarter.opponentGoals}</span>
                            </div>
                            {qTotal > 0 && (
                              <div className="scorers">
                                {quarter.ourGoals.map((goal, gIdx) => (
                                  <span key={gIdx} className="scorer">
                                    ⚽ {getPlayerName(goal.playerId)}
                                    {goal.count > 1 && ` ×${goal.count}`}
                                  </span>
                                ))}
                                {qOwnGoals > 0 && (
                                  <span className="scorer og">OG ×{qOwnGoals}</span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // 찬조 내역 화면
  if (activeTab === 'donations') {
    // 기간 필터링
    const getFilteredByPeriod = () => {
      if (donationPeriod === 'all') return donations;

      const today = new Date();
      const periodMonths = {
        '1month': 1,
        '3months': 3,
        '6months': 6,
        '1year': 12
      };

      const months = periodMonths[donationPeriod];
      if (!months) return donations;

      const cutoffDate = new Date(today);
      cutoffDate.setMonth(today.getMonth() - months);

      return donations.filter(donation => {
        const donationDate = new Date(donation.date + 'T00:00:00');
        return donationDate >= cutoffDate;
      });
    };

    // 검색 필터링
    const getFilteredDonations = () => {
      let filtered = getFilteredByPeriod();

      if (donationSearchQuery.trim()) {
        const query = donationSearchQuery.toLowerCase();
        filtered = filtered.filter(donation => {
          // 업체명 검색
          if (donation.businessName.toLowerCase().includes(query)) return true;

          // 참여자 이름 검색
          if (donation.contributors && donation.contributors.length > 0) {
            return donation.contributors.some(contrib => {
              const playerName = getPlayerName(contrib.playerId).toLowerCase();
              return playerName.includes(query);
            });
          }

          return false;
        });
      }

      // 날짜 내림차순 정렬 (최신순)
      return filtered.sort((a, b) => b.date.localeCompare(a.date));
    };

    const filteredDonations = getFilteredDonations();

    // 통계 계산
    const totalAmount = filteredDonations.reduce((sum, d) => sum + d.totalAmount, 0);
    const totalCount = filteredDonations.length;

    return (
      <div className="stats-page">
        <div className="page-header">
          <h2>찬조 내역</h2>
        </div>

        {/* 통계 요약 */}
        {donations.length > 0 && (
          <div className="donation-summary">
            <div className="summary-item">
              <span className="summary-label">총 건수</span>
              <span className="summary-value">{totalCount}건</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">총 금액</span>
              <span className="summary-value">{totalAmount.toLocaleString()}원</span>
            </div>
          </div>
        )}

        {/* 검색 */}
        <div className="search-section">
          <input
            type="text"
            placeholder="업체명 또는 참여자 검색"
            value={donationSearchQuery}
            onChange={(e) => setDonationSearchQuery(e.target.value)}
            className="search-input"
            aria-label="찬조 내역 검색"
          />
          {donationSearchQuery && (
            <button
              className="search-clear"
              onClick={() => setDonationSearchQuery('')}
              aria-label="검색어 지우기"
            >
              ×
            </button>
          )}
        </div>

        {/* 기간 필터 */}
        <div className="period-filter">
          <button
            className={donationPeriod === 'all' ? 'active' : ''}
            onClick={() => setDonationPeriod('all')}
          >
            전체
          </button>
          <button
            className={donationPeriod === '1month' ? 'active' : ''}
            onClick={() => setDonationPeriod('1month')}
          >
            1개월
          </button>
          <button
            className={donationPeriod === '3months' ? 'active' : ''}
            onClick={() => setDonationPeriod('3months')}
          >
            3개월
          </button>
          <button
            className={donationPeriod === '6months' ? 'active' : ''}
            onClick={() => setDonationPeriod('6months')}
          >
            6개월
          </button>
          <button
            className={donationPeriod === '1year' ? 'active' : ''}
            onClick={() => setDonationPeriod('1year')}
          >
            1년
          </button>
        </div>

        {filteredDonations.length === 0 ? (
          <div className="no-data">
            {donations.length === 0
              ? '등록된 찬조 내역이 없습니다.'
              : '검색 결과가 없습니다.'}
          </div>
        ) : (
          <div className="donation-list">
            {filteredDonations.map((donation) => (
              <div key={donation.id} className="donation-card">
                <div className="donation-header">
                  <span className="donation-date">{donation.date.replace(/^20/, '')}</span>
                  <span className="donation-amount">{donation.totalAmount.toLocaleString()}원</span>
                </div>
                <div className="donation-business">{donation.businessName}</div>
                {donation.contributors && donation.contributors.length > 0 && (
                  <div className="donation-contributors">
                    {donation.contributors.map((contrib, index) => (
                      <span key={index} className="contributor">
                        {getPlayerName(contrib.playerId)} {contrib.amount.toLocaleString()}원
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}

export default StatsPage;
